import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AUTH_REQUIRED_EVENT, type AuthRequiredDetail } from "@/lib/auth-api";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { AuthDialog } from "@/components/auth-dialog";
import { AppSidebar } from "@/components/app-sidebar";
import ChangePasswordsDialog from "@/components/ChangePasswordsDialog";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ChevronDown, KeyRound, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function NotFoundComponent() {
  return (
    <div className="fixed inset-0 z-[100] flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscas no existe o ha sido movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Volver a la web
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página no cargó
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo salió mal. Puedes intentar refrescar o volver al inicio.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Reintentar
          </button>
          <a
            href="/dashboard"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Idearia HealthCRM · Gestion para clinicas de fisioterapia" },
      { name: "description", content: "CRM simple para gestionar pacientes, profesionales y citas en clinicas de osteopatia, fisioterapia o masajes." },
      { property: "og:title", content: "Idearia HealthCRM · Gestion para clinicas de fisioterapia" },
      { property: "og:description", content: "CRM simple para gestionar pacientes, profesionales y citas en clinicas de osteopatia, fisioterapia o masajes." },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Idearia HealthCRM · Gestion para clinicas de fisioterapia" },
      { name: "twitter:description", content: "CRM simple para gestionar pacientes, profesionales y citas en clinicas de osteopatia, fisioterapia o masajes." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/77eb2915-8a6d-4435-b9fb-d0a97fcd5f7b/id-preview-b3db3844--b6350955-20ad-4413-be41-64d1187a04a3.lovable.app-1780657420745.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/77eb2915-8a6d-4435-b9fb-d0a97fcd5f7b/id-preview-b3db3844--b6350955-20ad-4413-be41-64d1187a04a3.lovable.app-1780657420745.png" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "stylesheet", href: appCss }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const IS_SPA_SHELL = import.meta.env.VITE_SPA_SHELL === "true";

function RootShell({ children }: { children: ReactNode }) {
  // Build estático (SPA): ya existe <html>/<body> en spa.html; renderizarlos de
  // nuevo dentro de #root produce un DOM anidado inválido que rompe los portales.
  if (IS_SPA_SHELL) {
    return (
      <>
        <HeadContent />
        {children}
      </>
    );
  }

  return (
    <html lang="es">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RootContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootContent() {
  const location = useRouterState({ select: (s) => s.location });
  const isRoutePending = useRouterState({ select: (s) => s.status === "pending" });
  const isUnknownRoute = useRouterState({
    select: (s) => s.matches.some((match) => match.globalNotFound),
  });
  const pathname = location.pathname;
  const { isAuthenticated, isCheckingAuth } = useAuth();
  const [loginReturnUrl, setLoginReturnUrl] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthRequired = (event: Event) => {
      setLoginReturnUrl((event as CustomEvent<AuthRequiredDetail>).detail.returnUrl);
    };

    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, []);

  if (loginReturnUrl) {
    return (
      <div className="min-h-screen bg-background">
        <AuthDialog
          open
          onOpenChange={(open) => {
            if (!open) setLoginReturnUrl(null);
          }}
          returnUrl={loginReturnUrl}
          onLoginSuccess={() => setLoginReturnUrl(null)}
        />
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  if (pathname === "/" || pathname === "/login") {
    return (
      <>
        <Outlet />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  // No se decide entre login y 404 hasta resolver tanto la ruta como la sesion.
  if (isRoutePending || isCheckingAuth) {
    return <div className="fixed inset-0 z-[100] min-h-screen bg-background" />;
  }

  if (isUnknownRoute) {
    return <NotFoundComponent />;
  }

  // Una ruta privada valida sin sesion solicita acceso y conserva su URL completa.
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <AuthDialog
          open
          onOpenChange={(open) => {
            if (!open) window.location.assign("/");
          }}
          returnUrl={location.href}
        />
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <div className="flex flex-1 flex-col">
          <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur">
            <SidebarTrigger />
            <div className="text-sm text-muted-foreground">HealthCRM</div>
            <HeaderAccount />
          </header>
          <main className="flex-1 p-6">
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster richColors position="top-right" />
    </SidebarProvider>
  );
}

function HeaderAccount() {
  const router = useRouter();
  const { user, logout } = useAuth();
  const [passwordsOpen, setPasswordsOpen] = useState(false);
  const displayName = user?.username ?? "Usuario";
  const initials =
    displayName
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "U";

  const handleLogout = async () => {
    await router.navigate({ to: "/" });
    logout();
  };

  return (
    <div className="ml-auto flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-auto gap-2 px-2 py-1.5"
            aria-label="Abrir menú de usuario"
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium sm:inline">{displayName}</span>
            <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => setPasswordsOpen(true)}>
            <KeyRound className="h-4 w-4" />
            Cambiar contraseña
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordsDialog open={passwordsOpen} onOpenChange={setPasswordsOpen} />
    </div>
  );
}
